/* ==========================================================================
   MAISON NOIR & CO — Lookbook gallery
   Full-bleed slide gallery with the Gucci PDP physics, rebuilt on the house
   tokens (ONE curve, ONE duration):

     · drag / swipe the slides (pointer events, rubber-band at the ends)
     · click an image to zoom into that exact point, then drag to pan
     · thumbnail rail + prev/next floating arrows + "1 / 4" pagination
     · progress bar, aria-live announcements, keyboard nav
     · sticky product summary card (title auto-scrolls when it overflows)

   Progressive: without JS the first slide simply shows, every control is a
   real <button>, and nothing is trapped hidden. Re-inits inside the theme
   editor on shopify:section:load and follows shopify:block:select.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTOR = '[data-mn-lookbook]';
  var DRAG_SLOP = 6; // px of movement before a press stops being a click

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
  }

  function MnLookbook(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-mn-viewport]');
    this.track = root.querySelector('[data-mn-track]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-mn-slide]'));
    this.zoomers = Array.prototype.slice.call(root.querySelectorAll('[data-mn-zoom]'));
    this.thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-mn-thumb]'));
    this.thumbList = root.querySelector('[data-mn-thumb-list]');
    this.prevButton = root.querySelector('[data-mn-prev]');
    this.nextButton = root.querySelector('[data-mn-next]');
    this.counter = root.querySelector('[data-mn-counter]');
    this.live = root.querySelector('[data-mn-live]');
    this.marquee = root.querySelector('[data-mn-marquee]');

    if (!this.viewport || !this.track || this.slides.length === 0) return;

    this.total = this.slides.length;
    this.index = 0;
    this.zoomScale = parseFloat(root.getAttribute('data-mn-zoom-level')) || 2.2;
    this.zoomEnabled = root.getAttribute('data-mn-zoom-enabled') !== 'false';
    this.labelTemplate = root.getAttribute('data-mn-label') || 'Image [i] of [n]';

    // Zoom state
    this.zoomed = false;
    this.panX = 0;
    this.panY = 0;
    this.maxPanX = 0;
    this.maxPanY = 0;

    // Drag state
    this.dragging = false;
    this.pointerId = null;
    this.axis = null;
    this.moved = false;

    this.bind();
    this.sync(true);
    this.measureMarquee();
    root.classList.add('is-ready');
  }

  MnLookbook.prototype.bind = function () {
    var self = this;

    this.onPointerDown = function (event) {
      self.pointerDown(event);
    };
    this.onPointerMove = function (event) {
      self.pointerMove(event);
    };
    this.onPointerUp = function (event) {
      self.pointerUp(event);
    };
    this.onKeyDown = function (event) {
      self.keyDown(event);
    };
    this.onResize = function () {
      self.resize();
    };

    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('pointermove', this.onPointerMove);
    this.viewport.addEventListener('pointerup', this.onPointerUp);
    this.viewport.addEventListener('pointercancel', this.onPointerUp);
    this.viewport.addEventListener('keydown', this.onKeyDown);
    // Native image drag would fight the swipe.
    this.viewport.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    // Zoom is decided on pointerup (see pointerUp), NOT on `click`: the viewport
    // captures the pointer on press, so the browser dispatches the follow-up
    // click to the viewport itself and the image's own click never fires.
    this.zoomers.forEach(function (zoomer) {
      zoomer.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        event.preventDefault();
        self.toggleZoom(zoomer, null);
      });
    });

    this.thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        self.goTo(i);
      });
    });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', function () {
        self.goTo(self.index - 1);
      });
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', function () {
        self.goTo(self.index + 1);
      });
    }

    window.addEventListener('resize', this.onResize);
  };

  /* ---------------------------------------------------------------- drag */

  MnLookbook.prototype.pointerDown = function (event) {
    if (event.button != null && event.button !== 0) return;

    this.dragging = true;
    this.moved = false;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.delta = 0;
    this.startTime = Date.now();
    this.axis = this.zoomed ? 'pan' : null;
    this.panOrigin = { x: this.panX, y: this.panY };
    // Remember which image (if any) the press started on — a clean tap on it
    // toggles the zoom at release time.
    this.pressZoomer =
      event.target && event.target.closest ? event.target.closest('[data-mn-zoom]') : null;

    this.track.style.transition = 'none';
    this.root.classList.add('is-dragging');

    if (this.viewport.setPointerCapture) {
      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (error) {
        /* capture is a nicety, never a requirement */
      }
    }
  };

  MnLookbook.prototype.pointerMove = function (event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;

    var dx = event.clientX - this.startX;
    var dy = event.clientY - this.startY;

    if (Math.abs(dx) > DRAG_SLOP || Math.abs(dy) > DRAG_SLOP) this.moved = true;

    if (this.axis === 'pan') {
      this.panX = clamp(this.panOrigin.x + dx, -this.maxPanX, this.maxPanX);
      this.panY = clamp(this.panOrigin.y + dy, -this.maxPanY, this.maxPanY);
      this.applyZoom(false);
      return;
    }

    if (this.axis === null) {
      if (!this.moved) return;
      // Vertical intent belongs to the page, not to us.
      this.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (this.axis === 'y') {
        this.endDrag();
        return;
      }
    }

    // Rubber-band past either end of the track.
    if ((this.offset <= 0 && dx > 0) || (this.offset >= this.maxOffset && dx < 0)) dx *= 0.35;

    this.delta = dx;
    this.track.style.setProperty('--mn-drag', dx + 'px');
  };

  MnLookbook.prototype.pointerUp = function (event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;

    var dx = this.delta || 0;
    var axis = this.axis;
    var tapped = !this.moved && event.type !== 'pointercancel' ? this.pressZoomer : null;
    this.pressZoomer = null;
    this.endDrag();
    this.moved = false;

    // A clean tap (no swipe, no pan) on an image toggles the zoom at that point.
    if (tapped) {
      this.toggleZoom(tapped, event);
      return;
    }

    if (axis !== 'x') return;

    var elapsed = Math.max(Date.now() - this.startTime, 1);
    var velocity = dx / elapsed; // px per ms
    // A fifth of a column is enough intent to advance one column.
    var threshold = (this.step || this.viewport.clientWidth || 1) * 0.2;

    if (dx <= -threshold || velocity < -0.5) this.goTo(this.index + 1);
    else if (dx >= threshold || velocity > 0.5) this.goTo(this.index - 1);
    else this.sync();
  };

  MnLookbook.prototype.endDrag = function () {
    this.dragging = false;
    this.pointerId = null;
    this.delta = 0;
    this.track.style.transition = '';
    this.track.style.setProperty('--mn-drag', '0px');
    this.root.classList.remove('is-dragging');
  };

  /* ---------------------------------------------------------------- zoom */

  MnLookbook.prototype.toggleZoom = function (zoomer, event) {
    if (!this.zoomEnabled) return;
    if (this.zoomed) {
      this.resetZoom();
      return;
    }

    var rect = zoomer.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var scale = this.zoomScale;
    var pointX = event ? event.clientX - rect.left - rect.width / 2 : 0;
    var pointY = event ? event.clientY - rect.top - rect.height / 2 : 0;

    this.maxPanX = ((scale - 1) * rect.width) / 2;
    this.maxPanY = ((scale - 1) * rect.height) / 2;
    // Translate so the clicked point stays exactly under the cursor.
    this.panX = clamp(pointX * (1 - scale), -this.maxPanX, this.maxPanX);
    this.panY = clamp(pointY * (1 - scale), -this.maxPanY, this.maxPanY);

    this.zoomed = true;
    this.activeZoomer = zoomer;
    this.axis = 'pan';
    this.applyZoom(true);
  };

  MnLookbook.prototype.applyZoom = function (animate) {
    var zoomer = this.activeZoomer;
    if (!zoomer) return;

    zoomer.style.transition = animate ? '' : 'none';
    zoomer.style.transform =
      'translate3d(' +
      this.panX +
      'px, ' +
      this.panY +
      'px, 0) scale(' +
      (this.zoomed ? this.zoomScale : 1) +
      ')';

    this.root.classList.toggle('is-zoomed', this.zoomed);
    zoomer.classList.toggle('is-zoomed', this.zoomed);
    zoomer.setAttribute('aria-pressed', this.zoomed ? 'true' : 'false');
  };

  MnLookbook.prototype.resetZoom = function () {
    if (!this.activeZoomer) return;
    this.zoomed = false;
    this.panX = 0;
    this.panY = 0;
    this.axis = null;
    this.applyZoom(true);
    this.activeZoomer = null;
  };

  /* ------------------------------------------------------------ movement */

  MnLookbook.prototype.goTo = function (index) {
    var next = clamp(index, 0, this.total - 1);
    if (next !== this.index) this.resetZoom();
    this.index = next;
    this.sync();
  };

  MnLookbook.prototype.keyDown = function (event) {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.goTo(this.index + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.goTo(this.index - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.goTo(0);
        break;
      case 'End':
        event.preventDefault();
        this.goTo(this.total - 1);
        break;
      case 'Escape':
        if (this.zoomed) {
          event.preventDefault();
          this.resetZoom();
        }
        break;
      default:
        break;
    }
  };

  MnLookbook.prototype.resize = function () {
    // Both the travel and the panning bounds are pixel measurements — drop the
    // zoom and re-measure rather than let either drift after a rotate/resize.
    this.resetZoom();
    this.sync(true);
    this.measureMarquee();
  };

  /* --------------------------------------------------------------- state */

  /* The column the carousel steps by: the narrowest slide on screen. With the
     reference layout (one full slide + half slides) that is half the frame;
     on mobile, where every slide is full width, it is the whole frame. */
  MnLookbook.prototype.columnWidth = function (frame) {
    var narrowest = 0;
    for (var i = 0; i < this.slides.length; i++) {
      var width = this.slides[i].offsetWidth;
      if (width > 0 && (narrowest === 0 || width < narrowest)) narrowest = width;
    }
    return narrowest || frame;
  };

  MnLookbook.prototype.sync = function (initial) {
    var self = this;
    var frame = this.viewport.clientWidth || 1;
    var last = this.slides[this.total - 1];
    var trackWidth = last ? last.offsetLeft + last.offsetWidth : frame;

    // The track travels ONE COLUMN per step — a column being the narrowest
    // slide (half the frame on desktop, the whole frame on mobile). That is
    // what makes the reference gallery slide "to the middle": from the full
    // opening shot, step 1 lands on its right half + image 2, step 2 on
    // images 2 + 3, and so on. Clamped so it never scrolls past its own end.
    this.step = this.columnWidth(frame);
    this.maxOffset = Math.max(trackWidth - frame, 0);
    this.offset = clamp(this.index * this.step, 0, this.maxOffset);

    this.track.style.setProperty('--mn-offset', -this.offset + 'px');
    this.root.style.setProperty('--mn-active', String(this.index));
    this.root.style.setProperty('--mn-total', String(this.total));
    this.root.style.setProperty(
      '--mn-progress',
      clamp(((this.offset + frame) / trackWidth) * 100, 0, 100) + '%'
    );

    this.slides.forEach(function (slide, i) {
      var isActive = i === self.index;
      // Two half slides share the frame — "visible" is a measurement, not an
      // index: anything overlapping the frame is on screen and reachable.
      var left = slide.offsetLeft;
      var isVisible = left + slide.offsetWidth > self.offset + 1 && left < self.offset + frame - 1;

      slide.classList.toggle('is-active', isActive);
      slide.classList.toggle('is-visible', isVisible);
      slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      slide.setAttribute('aria-current', isActive ? 'true' : 'false');

      var zoomer = self.zoomers[i];
      if (zoomer) zoomer.setAttribute('tabindex', isVisible && self.zoomEnabled ? '0' : '-1');
    });

    this.thumbs.forEach(function (thumb, i) {
      var isActive = i === self.index;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (this.counter) this.counter.textContent = String(this.index + 1);
    if (this.prevButton) this.prevButton.disabled = this.index === 0;
    if (this.nextButton) this.nextButton.disabled = this.index === this.total - 1;

    if (this.live && !initial) {
      this.live.textContent = this.labelTemplate
        .replace('[i]', String(this.index + 1))
        .replace('[n]', String(this.total));
    }

    this.scrollThumbIntoView(initial);
  };

  MnLookbook.prototype.scrollThumbIntoView = function (immediate) {
    var list = this.thumbList;
    var thumb = this.thumbs[this.index];
    if (!list || !thumb || list.scrollWidth <= list.clientWidth) return;

    var target = thumb.offsetLeft - (list.clientWidth - thumb.offsetWidth) / 2;
    var max = list.scrollWidth - list.clientWidth;
    var left = clamp(target, 0, max);

    if (list.scrollTo) {
      list.scrollTo({
        left: left,
        behavior: immediate || prefersReducedMotion() ? 'auto' : 'smooth',
      });
    } else {
      list.scrollLeft = left;
    }
  };

  /* ------------------------------------------------------------- marquee */

  MnLookbook.prototype.measureMarquee = function () {
    var wrap = this.marquee;
    if (!wrap) return;
    var text = wrap.querySelector('[data-mn-marquee-text]');
    if (!text) return;

    var overflow = text.scrollWidth - wrap.clientWidth;
    if (overflow > 2 && !prefersReducedMotion()) {
      wrap.style.setProperty('--mn-overflow', overflow + 'px');
      wrap.classList.add('is-scrolling');
    } else {
      wrap.classList.remove('is-scrolling');
    }
  };

  MnLookbook.prototype.destroy = function () {
    if (!this.viewport) return;
    this.viewport.removeEventListener('pointerdown', this.onPointerDown);
    this.viewport.removeEventListener('pointermove', this.onPointerMove);
    this.viewport.removeEventListener('pointerup', this.onPointerUp);
    this.viewport.removeEventListener('pointercancel', this.onPointerUp);
    this.viewport.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('resize', this.onResize);
  };

  /* ---------------------------------------------------------------- boot */

  function init(scope) {
    var roots = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].mnLookbook) continue;
      roots[i].mnLookbook = new MnLookbook(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor lifecycle
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var roots = event.target.querySelectorAll(SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].mnLookbook) roots[i].mnLookbook.destroy();
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest ? event.target.closest(SELECTOR) : null;
    if (!root || !root.mnLookbook) return;
    var index = parseInt(event.target.getAttribute('data-mn-index'), 10);
    if (!isNaN(index)) root.mnLookbook.goTo(index);
  });
})();
